import { Component } from "@angular/core";
import { AlertController, NavParams, ViewController } from "ionic-angular";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "providers/app-status/app-status";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { Subscription } from "rxjs";
import { AuthService } from "../../../app/auth/auth.service";
import { CareType } from "../../../models/patient-chart/care-type";
import { AnalyticsProvider } from "../../../providers/analytics/analytics";
import { ConfigurationService } from "../../../providers/configuration/configuration.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";
import { NavigationService } from "../../navigation.service";
import { CubeApplication } from "../navigation-header/cube-application";

/**
 * Generated class for the AppsPopoverPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */
const CHARTMEDS_SIGNON_URL: string =
  "https://www.chartmeds.com/ChartMeds/Authorize/SignonToken";
@Component({
  selector: "apps-popover",
  templateUrl: "apps-popover.html",
})
export class AppsPopoverPage {
  protected applications: CubeApplication[] = [];
  popoverClass: string = "popover"; // holds the css class for updating the popover bg color.
  isSystemAdmin: boolean = false;
  isNewReport: boolean = false; // toggle will use for new report page.
  subscriptions: Subscription[] = [];
  appSyncStatus: ConnectionStatusEnum;
  connectionStatusEnum = ConnectionStatusEnum;

  constructor(
    public viewCtrl: ViewController,
    public navParams: NavParams,
    private authSvc: AuthService,
    public navSvc: NavigationService,
    public analyticsService: AnalyticsProvider,
    public stateData: StateManagementService,
    private configurationService: ConfigurationService,
    public utilService: UtilityService,
    public navBarServiceProvider: NavBarServiceProvider,
    private alertCtrl: AlertController,
    public appStatus: AppStatus
  ) {
    this.applications = navParams.get("applications");
    this.appSyncStatus = this.appStatus.getAppStatus();
    // remove check of environment once all issues are resolved
    if (
      this.configurationService.getConfig().isLocal ||
      this.configurationService.getConfig().isDev ||
      this.configurationService.getConfig().isQA
    ) {
      // set css class (to update the background color) of apps-popover
      if (
        this.stateData.patientCareType &&
        this.stateData.patientCareType.toLowerCase() ===
          CareType[CareType.Palliative].toLowerCase() &&
        (this.utilService.isDynamicGlobalHeader(
          this.stateData.getAssessmentType(),
          "assessmentType"
        ) ||
          this.utilService.isDynamicGlobalHeader(
            this.stateData.getCurrentPage(),
            "currentPage"
          ) ||
          this.utilService.isDynamicGlobalHeader(
            this.stateData.getSectionId(),
            "sectionId"
          ))
      ) {
        this.popoverClass = "popover-palliative"; // see 'popover.scss' for more details on this css class
      }
    }

    this.subscriptions.push(
      this.stateData.showNewReport.subscribe((value) => {
        this.isNewReport = value;
      })
    );
  }

  close() {
    this.viewCtrl.dismiss();
  }

  launchApp(id: string) {
    this.navSvc.clearPatientSessionContext();
    switch (id.toLocaleLowerCase()) {
      case "location":
        this.navSvc.goToInternalPage("LocationGroup", {});
        break;

      case "billing":
        this.navSvc.goToInternalPage("ClaimManagementPage", {});
        break;

      case "patient":
        this.navSvc.goToInternalPage("ReferralsListPage", {});
        break;

      case "reports":
        this.navSvc.goToInternalPage("reports", {});
        break;

      case "hishopeicon":
        this.navSvc.goToInternalPage("HisPage", {});
        break;

      case "workforce":
        this.navSvc.goToInternalPage("WorkforcePage", {});
        break;

      case "facilities":
      case "legacybilling":
        //option 2: open a new tab
        window.open(
          this.configurationService.getConfig().welcomePageOld +
            "?tenantId=" +
            this.authSvc.getUser().tenantId,
          "_blank"
        );
        break;

      case "analytics":
        this.navSvc.goToInternalPage("OperationsDashboard", {});
        break;

      case "communitybereavement":
        this.navSvc.goToInternalPage("CommunityBereavementPage", {});
        break;

      case "ordermanagement":
        this.navSvc.goToInternalPage("ListOrdersPage", {});
        break;

      case "eprescribe":
        this.navSvc.goToInternalPage("EPrescribePage", {});
        break;

      case "legacyapp":
        window.open(
          this.configurationService.getConfig().welcomePageOld +
            "?tenantId=" +
            this.authSvc.getUser().tenantId,
          "_blank"
        );
        break;

      case "referralandintake":
        this.navSvc.goToInternalPage("IntakeQueuePage", {});
        break;

      case "chartmeds":
        this.goToChartMeds();
        break;

      case "westfax":
        this.goToWestFax();
        break;

      case "worldviewfax":
        this.goToWorldView();
        break;
    }
    this.close();
  }

  launchAnalytics() {
    const userName = this.authSvc.getUser().email;

    this.analyticsService.getAnalyticsURL(userName).subscribe(
      (data: { loginURL: string }) => {
        window.open(data.loginURL, "_blank");
      },
      (err) => {
        console.log(err);
      }
    );
  }

  showOrderEntry() {
    return this.isOffline()
      ? false
      : this.stateData.getSectionId() !== "orderEntry";
  }

  isOffline() {
    return this.appSyncStatus === this.connectionStatusEnum.Offline;
  }

  async goToChartMeds(): Promise<void> {
    const chartMedsAuthToken = await this.getChartMedsAuthToken();
    if (chartMedsAuthToken) {
      window.open(
        `${CHARTMEDS_SIGNON_URL}?SessionToken=${chartMedsAuthToken}`,
        "_blank"
      );
    } else {
      this.showUserPopup();
    }
  }

  getChartMedsAuthToken(): Promise<string> {
    return this.authSvc
      .getChartMedsAuthToken()
      .toPromise()
      .catch((err) => {
        console.error("Error while calling ChartMeds not available.");
        this.showUserPopup();
        throw err;
      });
  }

  showUserPopup() {
    let alert = this.alertCtrl.create({
      title: "User not in ChartMeds",
      message:
        "You are not a ChartMeds user. Please contact your Administrator to request access.",
      buttons: [
        {
          text: "OK",
        },
      ],
    });
    alert.present();
  }

  goToWestFax() {
    window.open("https://home.westfax.com/", "_blank");
  }

  goToWorldView() {
    const url = this.getWorldviewFaxUrl();
    window.open(url, "_blank");
  }

  getWorldviewFaxUrl(): string {
    const user = this.authSvc.getUser();
    const vendor = user?.organizations
      ?.find((org) => org.orgId === user.selectedOrg)
      ?.vendors?.find((v) => v.name?.toLowerCase().includes("worldview"));

    return vendor?.url || null;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/navigation/components/apps-popover/apps-popover.ts